/**
 * @voltxampere/engine — Independent voltage source AC stamp (Sprint 20)
 *
 * The voltage-source component carries optional `acMagnitude` and
 * `acPhase` (degrees) fields alongside its DC `voltage`. AC analysis
 * reads those AC fields:
 *
 *  - The MNA augmentation is identical to DC: the source contributes
 *    a `["vsource"]` augmented row whose unknown is the branch
 *    current. The KCL ±1 cells and KVL ±1 cells are the same `(1, 0j)`
 *    real values.
 *  - The RHS at the augmented row holds the *AC phasor*:
 *
 *        V_phasor = A · exp(j · phase_rad)
 *               = A · cos(phase_rad) + j · A · sin(phase_rad)
 *
 *    where `A = acMagnitude` and `phase_rad = acPhase · π/180`.
 *
 *  - When the AC fields are absent (DC-only source), the AC RHS is
 *    zero — the source acts as a short circuit at AC, which is the
 *    SPICE / ngspice convention for a `V` element without a `AC`
 *    keyword.
 *
 * Source-stepping does not touch AC stamps (only the DC pre-pass
 * scales independent sources during continuation). AC sees the
 * fully-on phasor.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { getTerminalNetId } from "../terminal-utils.js";
const DEG_TO_RAD = Math.PI / 180;
export const voltageSourceACStamp = (ctx, component) => {
    const pos = ctx.nodeIndex(getTerminalNetId(component, "pos"));
    const neg = ctx.nodeIndex(getTerminalNetId(component, "neg"));
    // Sprint 24 B1 — vSourceIndex returns the absolute MNA row
    // (`nodeCount + internalNodeCount + idx`); no manual offset add.
    const k = ctx.vSourceIndex(component.id);
    // Augmented MNA — KCL ±1 columns and KVL ±1 row, all real.
    ctx.addY(pos, k, 1, 0);
    ctx.addY(neg, k, -1, 0);
    ctx.addY(k, pos, 1, 0);
    ctx.addY(k, neg, -1, 0);
    // AC RHS phasor — read from optional voltage-source AC fields.
    const params = component.params;
    const acMagnitude = readNumber(params, "acMagnitude");
    if (acMagnitude === undefined || acMagnitude === 0) {
        // DC-only source: short circuit at AC (zero phasor).
        return;
    }
    const acPhaseDeg = readNumber(params, "acPhase") ?? 0;
    const phaseRad = acPhaseDeg * DEG_TO_RAD;
    const re = acMagnitude * Math.cos(phaseRad);
    const im = acMagnitude * Math.sin(phaseRad);
    ctx.addRhs(k, re, im);
};
function readNumber(params, key) {
    const v = params[key];
    return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}
//# sourceMappingURL=voltage-source.js.map